import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Check, X, Minus, Crown, RefreshCw, Paintbrush, ArrowRight, Info } from 'lucide-react';

const comparisonData = {
  de: {
    sectionTitle: 'Augenbrauentransplantation vs. Microblading & PMU',
    sectionSubtitle: 'Die einzige Methode mit echtem, dauerhaftem Haarwachstum',
    methods: [
      { key: 'transplant', name: 'Transplantation', icon: Crown },
      { key: 'microblading', name: 'Microblading', icon: Paintbrush },
      { key: 'pmu', name: 'Permanent Make-Up', icon: RefreshCw },
    ],
    categories: [
      {
        label: 'Haltbarkeit',
        transplant: { text: 'Dauerhaft (lebenslang)', score: 'best' },
        microblading: { text: '1–2 Jahre', score: 'bad' },
        pmu: { text: '2–5 Jahre', score: 'mid' },
      },
      {
        label: 'Natürlichkeit',
        transplant: { text: 'Echte Haare, 3D-Effekt', score: 'best' },
        microblading: { text: 'Flache Pigmentstriche', score: 'mid' },
        pmu: { text: 'Gemalter Look', score: 'bad' },
      },
      {
        label: 'Ergebnis',
        transplant: { text: 'Wachsende, stylbare Haare', score: 'best' },
        microblading: { text: 'Verblasst & verfärbt', score: 'bad' },
        pmu: { text: 'Verblasst mit der Zeit', score: 'bad' },
      },
      {
        label: 'Langzeitkosten',
        transplant: { text: 'Einmalige Investition', score: 'best' },
        microblading: { text: 'Alle 1–2 J. Auffrischung', score: 'bad' },
        pmu: { text: 'Alle 2–5 J. Auffrischung', score: 'mid' },
      },
      {
        label: 'Pflege',
        transplant: { text: 'Normales Styling möglich', score: 'best' },
        microblading: { text: 'Kein echtes Styling', score: 'bad' },
        pmu: { text: 'Kein echtes Styling', score: 'bad' },
      },
      {
        label: 'Narbenabdeckung',
        transplant: { text: 'Ja, vollständig', score: 'best' },
        microblading: { text: 'Nur bedingt', score: 'mid' },
        pmu: { text: 'Nur bedingt', score: 'mid' },
      },
    ],
    honestNotes: [
      {
        label: 'Sofort sichtbar?',
        transplant: { text: 'Endergebnis nach 6–12 Monaten', score: 'mid' },
        microblading: { text: 'Sofort sichtbar', score: 'best' },
        pmu: { text: 'Sofort sichtbar', score: 'best' },
      },
      {
        label: 'Anfangskosten',
        transplant: { text: 'Höhere Einmalinvestition', score: 'mid' },
        microblading: { text: 'Günstiger pro Sitzung', score: 'best' },
        pmu: { text: 'Günstiger pro Sitzung', score: 'best' },
      },
      {
        label: 'Trimmen nötig?',
        transplant: { text: 'Gelegentliches Trimmen', score: 'mid' },
        microblading: { text: 'Nicht nötig', score: 'best' },
        pmu: { text: 'Nicht nötig', score: 'best' },
      },
      {
        label: 'Eingriff',
        transplant: { text: 'Minimalinvasiv, Lokalanästhesie', score: 'mid' },
        microblading: { text: 'Nicht-chirurgisch', score: 'best' },
        pmu: { text: 'Nicht-chirurgisch', score: 'best' },
      },
    ],
    honestNotesTitle: 'Gut zu wissen',
    conclusion: 'Einmal investieren — ein Leben lang natürliche Augenbrauen',
    ctaText: 'Kostenlose Beratung vereinbaren',
  },
  en: {
    sectionTitle: 'Eyebrow Transplant vs. Microblading & PMU',
    sectionSubtitle: 'The only method with real, permanent hair growth',
    methods: [
      { key: 'transplant', name: 'Transplant', icon: Crown },
      { key: 'microblading', name: 'Microblading', icon: Paintbrush },
      { key: 'pmu', name: 'Permanent Make-Up', icon: RefreshCw },
    ],
    categories: [
      {
        label: 'Durability',
        transplant: { text: 'Permanent (lifelong)', score: 'best' },
        microblading: { text: '1–2 years', score: 'bad' },
        pmu: { text: '2–5 years', score: 'mid' },
      },
      {
        label: 'Natural Look',
        transplant: { text: 'Real hair, 3D effect', score: 'best' },
        microblading: { text: 'Flat pigment strokes', score: 'mid' },
        pmu: { text: 'Painted look', score: 'bad' },
      },
      {
        label: 'Result',
        transplant: { text: 'Growing, stylable hair', score: 'best' },
        microblading: { text: 'Fades & discolors', score: 'bad' },
        pmu: { text: 'Fades over time', score: 'bad' },
      },
      {
        label: 'Long-term Cost',
        transplant: { text: 'One-time investment', score: 'best' },
        microblading: { text: 'Refresh every 1–2 yrs', score: 'bad' },
        pmu: { text: 'Refresh every 2–5 yrs', score: 'mid' },
      },
      {
        label: 'Maintenance',
        transplant: { text: 'Normal styling possible', score: 'best' },
        microblading: { text: 'No real styling', score: 'bad' },
        pmu: { text: 'No real styling', score: 'bad' },
      },
      {
        label: 'Scar Coverage',
        transplant: { text: 'Yes, fully', score: 'best' },
        microblading: { text: 'Limited', score: 'mid' },
        pmu: { text: 'Limited', score: 'mid' },
      },
    ],
    honestNotes: [
      {
        label: 'Immediately visible?',
        transplant: { text: 'Final result after 6–12 months', score: 'mid' },
        microblading: { text: 'Immediately visible', score: 'best' },
        pmu: { text: 'Immediately visible', score: 'best' },
      },
      {
        label: 'Initial cost',
        transplant: { text: 'Higher one-time investment', score: 'mid' },
        microblading: { text: 'Cheaper per session', score: 'best' },
        pmu: { text: 'Cheaper per session', score: 'best' },
      },
      {
        label: 'Trimming needed?',
        transplant: { text: 'Occasional trimming', score: 'mid' },
        microblading: { text: 'Not needed', score: 'best' },
        pmu: { text: 'Not needed', score: 'best' },
      },
      {
        label: 'Procedure',
        transplant: { text: 'Minimally invasive, local anesthesia', score: 'mid' },
        microblading: { text: 'Non-surgical', score: 'best' },
        pmu: { text: 'Non-surgical', score: 'best' },
      },
    ],
    honestNotesTitle: 'Good to know',
    conclusion: 'Invest once — enjoy natural eyebrows for life',
    ctaText: 'Book free consultation',
  },
};

const ScoreIcon = ({ score, isDark }) => {
  if (score === 'best') return <Check className="w-5 h-5 text-emerald-500" />;
  if (score === 'mid') return <Minus className="w-5 h-5 text-amber-500" />;
  return <X className="w-5 h-5 text-red-400" />;
};

const EyebrowComparison = () => {
  const { features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();

  if (!features?.showEyebrowPatterns) return null;

  const t = comparisonData[language] || comparisonData.de;

  return (
    <section
      className={`py-20 md:py-28 relative overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0a0a0a]' : 'bg-gradient-to-b from-gray-50 to-white'
      }`}
      data-testid="eyebrow-comparison-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <h2
            className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
            data-testid="comparison-title"
          >
            {t.sectionTitle}
          </h2>
          <p
            className={`text-base lg:text-lg max-w-2xl mx-auto ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            {t.sectionSubtitle}
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block">
          <div
            className={`rounded-2xl overflow-hidden border ${
              isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white'
            }`}
          >
            {/* Table Header */}
            <div className={`grid grid-cols-4 ${isDark ? 'bg-white/[0.04]' : 'bg-gray-50'}`}>
              <div className={`p-5 font-semibold text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {language === 'de' ? 'Kriterium' : 'Criteria'}
              </div>
              {t.methods.map((method) => {
                const Icon = method.icon;
                const isTransplant = method.key === 'transplant';
                return (
                  <div
                    key={method.key}
                    className={`p-5 text-center font-semibold text-sm ${
                      isTransplant
                        ? isDark
                          ? 'text-[#7BA7C2] bg-[#7BA7C2]/5'
                          : 'text-[#5A8BA6] bg-[#5A8BA6]/5'
                        : isDark
                          ? 'text-gray-300'
                          : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Icon className="w-4 h-4" />
                      {method.name}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Table Rows */}
            {t.categories.map((cat, idx) => (
              <div
                key={cat.label}
                className={`grid grid-cols-4 transition-colors ${
                  isDark
                    ? `border-t border-white/5 ${idx % 2 === 0 ? '' : 'bg-white/[0.01]'}`
                    : `border-t border-gray-100 ${idx % 2 === 0 ? '' : 'bg-gray-50/50'}`
                }`}
              >
                <div className={`p-5 font-medium text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {cat.label}
                </div>
                {['transplant', 'microblading', 'pmu'].map((method) => {
                  const cell = cat[method];
                  const isTransplant = method === 'transplant';
                  return (
                    <div
                      key={method}
                      className={`p-5 text-center text-sm ${
                        isTransplant
                          ? isDark
                            ? 'bg-[#7BA7C2]/5'
                            : 'bg-[#5A8BA6]/5'
                          : ''
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <ScoreIcon score={cell.score} isDark={isDark} />
                        <span className={`${
                          cell.score === 'best'
                            ? isDark ? 'text-white font-medium' : 'text-gray-900 font-medium'
                            : isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {cell.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* "Gut zu wissen" Separator + Honest Notes */}
            <div className={`grid grid-cols-4 ${isDark ? 'bg-white/[0.04] border-t border-white/10' : 'bg-amber-50/60 border-t-2 border-amber-200/50'}`}>
              <div className={`p-4 col-span-4 flex items-center gap-2 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                <Info className="w-4 h-4" />
                <span className="text-sm font-semibold">{t.honestNotesTitle}</span>
                <span className={`text-xs ${isDark ? 'text-amber-400/60' : 'text-amber-600/70'}`}>
                  — {language === 'de' ? 'Wir sind transparent' : 'We believe in transparency'}
                </span>
              </div>
            </div>
            {t.honestNotes.map((cat, idx) => (
              <div
                key={cat.label}
                className={`grid grid-cols-4 transition-colors ${
                  isDark
                    ? `border-t border-white/5 ${idx % 2 === 0 ? 'bg-amber-900/[0.03]' : 'bg-amber-900/[0.06]'}`
                    : `border-t border-amber-100/50 ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`
                }`}
              >
                <div className={`p-5 font-medium text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {cat.label}
                </div>
                {['transplant', 'microblading', 'pmu'].map((method) => {
                  const cell = cat[method];
                  const isTransplant = method === 'transplant';
                  return (
                    <div
                      key={method}
                      className={`p-5 text-center text-sm ${
                        isTransplant
                          ? isDark
                            ? 'bg-[#7BA7C2]/5'
                            : 'bg-[#5A8BA6]/[0.03]'
                          : ''
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <ScoreIcon score={cell.score} isDark={isDark} />
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                          {cell.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Compact Table */}
        <div className="md:hidden">
          <div className={`rounded-2xl overflow-hidden border ${
            isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white'
          }`}>
            {/* Mobile Table Header */}
            <div className={`grid grid-cols-[1fr_60px_60px_60px] ${isDark ? 'bg-white/[0.04]' : 'bg-gray-50'}`}>
              <div className={`px-3 py-3 text-xs font-semibold ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              {t.methods.map((method) => {
                const Icon = method.icon;
                const isTransplant = method.key === 'transplant';
                return (
                  <div
                    key={method.key}
                    className={`px-1 py-3 text-center ${
                      isTransplant
                        ? isDark ? 'bg-[#7BA7C2]/8' : 'bg-[#5A8BA6]/5'
                        : ''
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 mx-auto mb-0.5 ${
                      isTransplant
                        ? isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'
                        : isDark ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                    <span className={`text-[10px] font-semibold leading-tight block ${
                      isTransplant
                        ? isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'
                        : isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {method.key === 'transplant'
                        ? (language === 'de' ? 'Trans.' : 'Trans.')
                        : method.key === 'microblading'
                          ? 'Micro.'
                          : 'PMU'}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Mobile Advantage Rows */}
            {t.categories.map((cat, idx) => (
              <div
                key={cat.label}
                className={`grid grid-cols-[1fr_60px_60px_60px] items-center ${
                  isDark
                    ? `border-t border-white/5 ${idx % 2 !== 0 ? 'bg-white/[0.01]' : ''}`
                    : `border-t border-gray-100 ${idx % 2 !== 0 ? 'bg-gray-50/50' : ''}`
                }`}
              >
                <div className={`px-3 py-2.5 text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {cat.label}
                  <span className={`block text-[10px] font-normal mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {cat.transplant.text}
                  </span>
                </div>
                {['transplant', 'microblading', 'pmu'].map((method) => {
                  const cell = cat[method];
                  const isTransplant = method === 'transplant';
                  return (
                    <div
                      key={method}
                      className={`py-2.5 flex justify-center ${
                        isTransplant ? isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/[0.03]' : ''
                      }`}
                    >
                      <ScoreIcon score={cell.score} isDark={isDark} />
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Gut zu wissen Separator */}
            <div className={`grid grid-cols-1 ${isDark ? 'bg-white/[0.04] border-t border-white/10' : 'bg-amber-50/60 border-t-2 border-amber-200/50'}`}>
              <div className={`px-3 py-2.5 flex items-center gap-1.5 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                <Info className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">{t.honestNotesTitle}</span>
                <span className={`text-[10px] ${isDark ? 'text-amber-400/60' : 'text-amber-600/70'}`}>
                  — {language === 'de' ? 'Wir sind transparent' : 'Transparency'}
                </span>
              </div>
            </div>

            {/* Honest Note Rows */}
            {t.honestNotes.map((cat, idx) => (
              <div
                key={cat.label}
                className={`grid grid-cols-[1fr_60px_60px_60px] items-center ${
                  isDark
                    ? `border-t border-white/5 ${idx % 2 === 0 ? 'bg-amber-900/[0.03]' : 'bg-amber-900/[0.06]'}`
                    : `border-t border-amber-100/50 ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`
                }`}
              >
                <div className={`px-3 py-2.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {cat.label}
                  <span className={`block text-[10px] font-normal mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    {cat.transplant.text}
                  </span>
                </div>
                {['transplant', 'microblading', 'pmu'].map((method) => {
                  const cell = cat[method];
                  const isTransplant = method === 'transplant';
                  return (
                    <div
                      key={method}
                      className={`py-2.5 flex justify-center ${
                        isTransplant ? isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/[0.03]' : ''
                      }`}
                    >
                      <ScoreIcon score={cell.score} isDark={isDark} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Conclusion */}
        <div className="mt-12 text-center">
          <p className={`text-lg md:text-xl font-semibold mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {t.conclusion}
          </p>
          <button
            onClick={() => {
              const el = document.querySelector('#kontakt');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-medium transition-all duration-300 hover:scale-105 bg-[#7BA7C2] hover:bg-[#6b97b2] shadow-lg hover:shadow-xl"
            data-testid="comparison-cta-btn"
          >
            {t.ctaText}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default EyebrowComparison;
